import { useEffect, useRef, useState } from "react";

interface LightTunnelProps {
  active: boolean;
  onNavigate: () => void;   // トンネルが最大輝度に達したら呼ぶ
  onComplete: () => void;   // アニメーション全体が終わったら呼ぶ（使わないが互換用）
}

export default function LightTunnel({ active, onNavigate }: LightTunnelProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const animRef = useRef<number>(0);
  const progressRef = useRef(0);       // 0 → 1 でトンネルが深まる
  const navigatedRef = useRef(false);
  const [visible, setVisible] = useState(false);
  const [textPhase, setTextPhase] = useState<"hidden" | "show" | "fade">("hidden");

  useEffect(() => {
    if (!active) return;
    setVisible(true);

    // テキスト表示タイムライン
    const t1 = setTimeout(() => setTextPhase("show"), 600);
    const t2 = setTimeout(() => setTextPhase("fade"), 3800);
    // navigate はトンネルが白く染まる直前（4.5秒後）
    const t3 = setTimeout(() => {
      if (!navigatedRef.current) {
        navigatedRef.current = true;
        onNavigate();
      }
    }, 4600);

    return () => { clearTimeout(t1); clearTimeout(t2); clearTimeout(t3); };
  }, [active, onNavigate]);

  useEffect(() => {
    if (!active || !visible) return;

    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    canvas.width = window.innerWidth;
    canvas.height = window.innerHeight;

    const cx = canvas.width / 2;
    const cy = canvas.height / 2;

    // トンネルリング（奥から手前に向かって広がる光の輪）
    interface Ring {
      z: number;       // 0=奥, 1=手前
      speed: number;
      opacity: number;
      hue: number;     // 色相（金→白）
    }

    const rings: Ring[] = Array.from({ length: 24 }, (_, i) => ({
      z: i / 24,
      speed: 0.004 + Math.random() * 0.003,
      opacity: 0.3 + Math.random() * 0.4,
      hue: 40 + Math.random() * 20,
    }));

    // 光の粒子（奥の消失点から手前に飛んでくる）
    interface Particle {
      angle: number;
      dist: number;    // 消失点からの距離（0=中心, 1=画面端）
      speed: number;
      size: number;
      opacity: number;
      hue: number;
    }

    const particles: Particle[] = Array.from({ length: 120 }, () => ({
      angle: Math.random() * Math.PI * 2,
      dist: Math.random(),
      speed: 0.008 + Math.random() * 0.012,
      size: 0.5 + Math.random() * 2,
      opacity: 0.4 + Math.random() * 0.5,
      hue: 35 + Math.random() * 30,
    }));

    let t = 0;
    const totalDuration = 280; // フレーム数（約4.7秒 @60fps）

    function draw() {
      t++;
      const rawProgress = Math.min(t / totalDuration, 1);
      // イーズイン：最初はゆっくり、後半加速
      const p = rawProgress < 0.5
        ? 2 * rawProgress * rawProgress
        : 1 - Math.pow(-2 * rawProgress + 2, 2) / 2;
      progressRef.current = p;

      ctx.clearRect(0, 0, canvas.width, canvas.height);

      // ── 背景：暗闇から金の光へ ──
      const bgAlpha = Math.min(p * 1.2, 1);
      ctx.fillStyle = `rgba(0,0,0,${bgAlpha})`;
      ctx.fillRect(0, 0, canvas.width, canvas.height);

      // ── トンネルの奥行き感：放射状グラデーション ──
      const tunnelRadius = Math.max(canvas.width, canvas.height) * (0.15 + p * 0.85);
      const tunnelGrd = ctx.createRadialGradient(cx, cy, 0, cx, cy, tunnelRadius);
      const innerAlpha = 0.05 + p * 0.35;
      const outerAlpha = 0;
      tunnelGrd.addColorStop(0, `rgba(255, 240, 180, ${innerAlpha})`);
      tunnelGrd.addColorStop(0.3, `rgba(255, 210, 100, ${innerAlpha * 0.6})`);
      tunnelGrd.addColorStop(0.7, `rgba(255, 180, 40, ${innerAlpha * 0.2})`);
      tunnelGrd.addColorStop(1, `rgba(255, 160, 0, ${outerAlpha})`);
      ctx.beginPath();
      ctx.arc(cx, cy, tunnelRadius, 0, Math.PI * 2);
      ctx.fillStyle = tunnelGrd;
      ctx.fill();

      // ── 光の輪（トンネルのリング） ──
      rings.forEach((ring) => {
        ring.z += ring.speed * (1 + p * 2);
        if (ring.z > 1) ring.z = 0;

        // z=0が奥（小さい）、z=1が手前（大きい）
        const perspective = ring.z * ring.z; // 非線形で奥行き感
        const radius = perspective * Math.max(canvas.width, canvas.height) * 0.7;
        const lineWidth = (0.5 + perspective * 3) * (1 + p);
        const alpha = ring.opacity * (1 - ring.z) * (0.3 + p * 0.7);

        if (radius < 2) return;

        ctx.beginPath();
        ctx.arc(cx, cy, radius, 0, Math.PI * 2);
        ctx.strokeStyle = `hsla(${ring.hue}, 80%, ${60 + p * 30}%, ${alpha})`;
        ctx.lineWidth = lineWidth;
        ctx.stroke();
      });

      // ── 光の粒子（消失点から飛んでくる） ──
      particles.forEach((pt) => {
        pt.dist += pt.speed * (1 + p * 3);
        if (pt.dist > 1.2) {
          pt.dist = 0.01 + Math.random() * 0.05;
          pt.angle = Math.random() * Math.PI * 2;
        }

        const x = cx + Math.cos(pt.angle) * pt.dist * canvas.width * 0.6;
        const y = cy + Math.sin(pt.angle) * pt.dist * canvas.height * 0.6;
        const size = pt.size * (0.2 + pt.dist * 2) * (1 + p);
        const alpha = pt.opacity * (1 - pt.dist * 0.5) * (0.2 + p * 0.8);

        ctx.beginPath();
        ctx.arc(x, y, Math.max(size, 0.3), 0, Math.PI * 2);
        ctx.fillStyle = `hsla(${pt.hue}, 90%, ${70 + p * 20}%, ${alpha})`;
        ctx.fill();
      });

      // ── 中央の消失点グロー ──
      const vanishSize = (20 + p * 80) * (1 + Math.sin(t * 0.08) * 0.1);
      const vanishGrd = ctx.createRadialGradient(cx, cy, 0, cx, cy, vanishSize);
      vanishGrd.addColorStop(0, `rgba(255, 255, 240, ${0.6 + p * 0.4})`);
      vanishGrd.addColorStop(0.3, `rgba(255, 230, 150, ${0.3 + p * 0.3})`);
      vanishGrd.addColorStop(1, "rgba(255, 200, 80, 0)");
      ctx.beginPath();
      ctx.arc(cx, cy, vanishSize, 0, Math.PI * 2);
      ctx.fillStyle = vanishGrd;
      ctx.fill();

      // ── 最終フラッシュ（p > 0.85 で白く染まる） ──
      if (p > 0.85) {
        const flashAlpha = (p - 0.85) / 0.15;
        ctx.fillStyle = `rgba(255, 248, 220, ${flashAlpha * 0.95})`;
        ctx.fillRect(0, 0, canvas.width, canvas.height);
      }

      animRef.current = requestAnimationFrame(draw);
    }

    animRef.current = requestAnimationFrame(draw);
    return () => cancelAnimationFrame(animRef.current);
  }, [active, visible]);

  if (!visible) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center"
      style={{ pointerEvents: "all" }}
    >
      {/* トンネルキャンバス */}
      <canvas
        ref={canvasRef}
        className="absolute inset-0"
        style={{ width: "100vw", height: "100vh" }}
      />

      {/* テキスト */}
      <div
        className="relative z-10 text-center px-8"
        style={{ maxWidth: "560px" }}
      >
        {/* 上の金線 */}
        <div
          className="mx-auto mb-8"
          style={{
            width: textPhase === "show" ? "70px" : "0px",
            height: "1px",
            background: "rgba(255, 230, 140, 0.5)",
            transition: "width 1.4s ease 0.2s",
          }}
        />

        {/* メインテキスト */}
        <p
          style={{
            fontFamily: "'Cormorant Garamond', serif",
            color: "rgba(255, 245, 210, 0.85)",
            fontSize: "clamp(1.05rem, 2.8vw, 1.45rem)",
            fontWeight: 300,
            lineHeight: 2.3,
            letterSpacing: "0.13em",
            fontStyle: "italic",
            opacity: textPhase === "show" ? 1 : 0,
            transform: textPhase === "show" ? "translateY(0)" : "translateY(10px)",
            transition: textPhase === "fade"
              ? "opacity 1.2s ease, transform 1.2s ease"
              : "opacity 1.6s ease 0.4s, transform 1.6s ease 0.4s",
            textShadow: "0 0 30px rgba(255, 200, 80, 0.4)",
          }}
        >
          金庫の鍵のパスワードは、
          <br />
          <span
            style={{
              color: "rgba(255, 220, 100, 0.95)",
              textShadow: "0 0 20px rgba(255, 200, 60, 0.6)",
            }}
          >
            既にもう知っているはずです。
          </span>
        </p>

        {/* サブテキスト */}
        <p
          style={{
            fontFamily: "'Cormorant Garamond', serif",
            color: "rgba(255, 255, 255, 0.25)",
            fontSize: "10px",
            letterSpacing: "0.45em",
            marginTop: "20px",
            opacity: textPhase === "show" ? 1 : 0,
            transition: "opacity 1.6s ease 1.4s",
          }}
        >
          螺旋は、最初からそこにあった
        </p>

        {/* 下の金線 */}
        <div
          className="mx-auto mt-8"
          style={{
            width: textPhase === "show" ? "36px" : "0px",
            height: "1px",
            background: "rgba(255, 220, 100, 0.3)",
            transition: "width 1.4s ease 0.5s",
          }}
        />
      </div>
    </div>
  );
}
